import { jwtDecode } from "jwt-decode"
import type { User } from "@/shared/schemas/auth-schema"
import { createDomainCookieUtils, type DomainCookieUtils } from "./cookie-utils"
import { 
  setEncryptedSessionStorage,
  getEncryptedSessionStorage,
  removeEncryptedSessionStorage
} from "./crypto-utils"

// JWT token interface
export interface JwtPayload {
  exp: number
  sub: string
  [key: string]: unknown
}

// Domain auth utilities interface
export interface DomainAuthUtils {
  setAccessToken: (token: string) => void
  getAccessToken: () => string | null
  setRefreshToken: (token: string) => void
  getRefreshToken: () => string | null
  setUser: (user: User) => void
  getUser: () => User | null
  clearAuth: () => void
  isTokenValid: (token: string | null) => boolean
  getTokenExpiration: (token: string | null) => number | null
  isAuthenticated: () => boolean
}

/**
 * Factory function to create domain-specific auth utilities
 * @param namespace - Domain namespace (e.g., 'admin', 'fingerprinting', 'immigration')
 * @returns DomainAuthUtils with namespaced storage keys
 */
export function createDomainAuthUtils(namespace: string): DomainAuthUtils {
  const cookieUtils: DomainCookieUtils = createDomainCookieUtils(namespace)
  const userStorageKey = `${namespace}_portal_user`

  const utils: DomainAuthUtils = {
    setAccessToken: (token: string): void => {
      cookieUtils.setAccessToken(token)
    },

    getAccessToken: (): string | null => {
      return cookieUtils.getAccessToken() || null
    },

    setRefreshToken: (token: string): void => {
      cookieUtils.setRefreshToken(token)
    },

    getRefreshToken: (): string | null => {
      return cookieUtils.getRefreshToken() || null
    },

    setUser: (user: User): void => {
      setEncryptedSessionStorage(userStorageKey, user)
    },

    getUser: (): User | null => {
      return getEncryptedSessionStorage<User>(userStorageKey)
    },

    clearAuth: (): void => {
      cookieUtils.clearAuthCookies()
      removeEncryptedSessionStorage(userStorageKey)
    },

    isTokenValid: (token: string | null): boolean => {
      if (!token) return false
      try {
        const decoded = jwtDecode<JwtPayload>(token)
        const currentTime = Date.now() / 1000
        return decoded.exp > currentTime
      } catch {
        return false
      }
    },

    getTokenExpiration: (token: string | null): number | null => {
      if (!token) return null
      try {
        const decoded = jwtDecode<JwtPayload>(token)
        return decoded.exp
      } catch {
        return null
      }
    },

    isAuthenticated: (): boolean => {
      const token = utils.getAccessToken()
      return utils.isTokenValid(token)
    },
  }

  return utils
}
